import React from "react";
import Reveal from "./Reveal";

/**
 * Consistent vertical rhythm + max width for every section on the site.
 * Section spacing comes from --dl-section-y so it scales fluidly instead
 * of stepping at breakpoints.
 */
export const Section = ({
  children,
  id,
  as: Tag = "section",
  tone = "light",
  className = "",
  containerClassName = "",
  ...rest
}) => {
  const tones = {
    light: "bg-white",
    subtle: "bg-ink-50",
    tint: "bg-brand-50",
    dark: "bg-ink-950 text-ink-200",
    none: "",
  };

  return (
    <Tag
      id={id}
      className={`section-padding relative ${tones[tone] ?? ""} ${className}`.trim()}
      {...rest}
    >
      <div className={`container-custom relative ${containerClassName}`.trim()}>
        {children}
      </div>
    </Tag>
  );
};

/**
 * The small uppercase label above a heading. On dark backgrounds pass
 * tone="dark".
 */
export const Eyebrow = ({ children, tone = "light", className = "" }) => (
  <span
    className={`eyebrow ${
      tone === "dark" ? "text-brand-300" : "text-brand-600"
    } ${className}`.trim()}
  >
    <span
      aria-hidden="true"
      className={`h-px w-6 ${tone === "dark" ? "bg-brand-400/60" : "bg-brand-400"}`}
    />
    {children}
  </span>
);

/**
 * Section heading block: eyebrow + h2 + optional lead paragraph.
 * `align` controls left vs centred; left is the default because it reads
 * as more editorial and less like a template.
 */
export const SectionHeading = ({
  eyebrow,
  title,
  lead,
  align = "left",
  tone = "light",
  maxWidth = "max-w-3xl",
  className = "",
  headingId,
  as: HeadingTag = "h2",
}) => {
  const centred = align === "center";

  return (
    <Reveal
      className={`${centred ? `${maxWidth} mx-auto text-center` : maxWidth} ${className}`.trim()}
    >
      {eyebrow ? <Eyebrow tone={tone}>{eyebrow}</Eyebrow> : null}
      <HeadingTag
        id={headingId}
        className={`mt-5 text-display-sm md:text-display font-bold font-bricolage ${
          tone === "dark" ? "text-white" : "text-ink-900"
        }`}
      >
        {title}
      </HeadingTag>
      {lead ? (
        <p
          className={`mt-5 text-base sm:text-lg leading-relaxed ${
            tone === "dark" ? "text-ink-300" : "text-ink-500"
          } ${centred ? "mx-auto" : ""}`}
        >
          {lead}
        </p>
      ) : null}
    </Reveal>
  );
};

export default Section;
